/* eslint-disable complexity */
import { batch } from 'react-redux'
import qs from 'query-string'
import { hasOperationMetadata, shouldSetPin } from 'utils/card'
import { redirectTo, goTo } from 'utils/navigate'
import { get } from 'lodash-es'
import { handleUnexpectedError } from 'utils/error/error-handler'
import FetchError from 'utils/error/fetch-error'
import { constructStepUpRedirectUrl, isRedirectFromStepUp } from 'utils/risk'
import { getQueryParams, getQueryParam } from 'utils/query'
import { createReplaceCardFlowUrl, createSetPinFlowUrl } from 'utils/flow'
import { getProductQueryParam, isValidProductName } from 'utils/utils'
import { FLOW_STEPS } from '@/constants/flow-steps'
import {
  getProductSystemNameByAlias,
  applicationStatus,
  OPERATIONS,
  SUB_OPERATIONS,
  isCardShipped,
  isCardNotShipped,
  CARD_ACTIVATION_ERROR_CODES,
  NEED_MORE_DATA_CODES,
  NEED_MORE_INFO_VALUES,
  PRODUCT_NAMES,
} from 'universal/cfs-card'
import { INTENT_TYPES } from 'universal/constants'
import {
  setPageError,
  updateCvv,
  updateDobSsn,
  updateCreatePin,
  updateConfirmPin,
  setPinValidated,
  clearAutoSubmit,
  setActivateSubmitFailed,
  selectActivateSubmitFailed,
} from '@/features/activate-card/activate-card-slice'
import { successfulFetch } from '@/features/ui/ui-slice'
import { REPLACE_CARD_REASONS } from '@/constants/card'
import {
  getCardManagementPath,
  generateCipRedirectUrl,
  GET_PP_APP,
  PP_SUMMARY,
} from 'universal/routes'

import { FLOWS } from 'universal/ucs'
import { getPayPalUrl, serverData } from 'utils/server-data'
import { isFromEnrollmentFlow } from 'utils/screens/enroll-card'
import { isInstantCard } from '@/screens/bdmc-summary/CardDetails/utils/cardHelpers'
import {
  browserRedirectTo,
  prepBdmcSummaryLink,
} from '@/screens/bdmc-summary/CardDetails/utils/urlUtils'
import { UCS_STUB_URL } from '@/screens/bdmc-summary/CardDetails/utils/constants'

const { BUSINESS_DEBIT_CARD } = PRODUCT_NAMES

/** True for BDMC activate (consolidation / business debit). PPDC must keep develop behavior. */
export function isBdmcActivateContext(card?: { productName?: string; product_name?: string }) {
  const productName = card?.productName || card?.product_name || getQueryParam('productName') || ''
  return serverData?.isBdmcExperience === true || productName === BUSINESS_DEBIT_CARD
}

const { MANAGE, ACTIVATE_CARD } = FLOWS
const {
  ENTER_CVV_STEP,
  CREATE_PIN_STEP,
  CARD_NOT_RECEIVED_STEP,
  NEED_MORE_DATA_STEP,
  MANUAL_REVIEW_STEP,
  DENIED_STEP,
  INELIGIBLE_STEP,
  CONFIRM_INFO_STEP,
  DOB_SSN_STEP,
  ACTIVATE_SUCCESS_STEP,
} = FLOW_STEPS.ACTIVATE
const { STEPUP_AUTH, NEED_MORE_DATA, MANUAL_REVIEW, DENIED } = applicationStatus
const { INVALID_CVV, MISMATCHED_CVV } = CARD_ACTIVATION_ERROR_CODES
const { APPLICATIONS } = OPERATIONS
const { ACTIVATE } = SUB_OPERATIONS
const { LOST_OR_STOLEN } = REPLACE_CARD_REASONS
const { NEED_MORE_INFO } = NEED_MORE_DATA_CODES

export const INVALID_PRODUCT_NAME = 'INVALID_PRODUCT_NAME'

export const INVALID_CVV_ERROR = 'invalidCvv'

export const PIN_INPUT_DEFAULT_LENGTH = 4

export const INVALID_AGE_DENIED_VALUE = 'INVALID_AGE'

/** StepSwitch name wrapping confirm-info and card-not-received in ActivateCardFlow. */
export const ACTIVATE_STEP3_SWITCH_NAME = 'step3'

/** StepSwitch name wrapping activate success and terminal error pages. */
export const ACTIVATE_LAST_STEP_SWITCH_NAME = 'lastStep'

export function resolveActivateCardId(card) {
  return card?.cardId || card?.card_id || ''
}

/**
 * Named-step navigation for activate flows. Linear stepper.nextStep() lands on create-pin
 * after PIN/DOB steps were inserted between CVV and confirm (legacy debitcardnodeweb had no
 * extra steps in that path).
 */
export function navigateActivateFlowStep(stepper, stepName, switchStepName?) {
  if (!stepper) {
    return
  }

  if (switchStepName) {
    stepper.goToNextStep(stepName, switchStepName)
    return
  }

  const nestedLastStepTargets = [
    ACTIVATE_SUCCESS_STEP,
    NEED_MORE_DATA_STEP,
    MANUAL_REVIEW_STEP,
    DENIED_STEP,
    INELIGIBLE_STEP,
  ]

  if (nestedLastStepTargets.includes(stepName)) {
    stepper.goToNextStep(ACTIVATE_LAST_STEP_SWITCH_NAME, stepName)
    return
  }

  stepper.goToNextStep(stepName)
}

/**
 * Top-level PPDC ActivateCardFlow indices (no createPin/dobSsn between CVV and step3).
 * BDMC prepends cardNotDelivered and inserts PIN/DOB — see activateFirstStep.ts.
 */
export const ACTIVATE_FLOW_STEP_INDEX = {
  ENTER_CVV: 1,
  STEP3_SWITCH: 2,
}

function getApplyResultDetails(applyCard) {
  return applyCard?.resultDetails || applyCard?.result_details || {}
}

function isPrimaryActivateCard(card) {
  return card?.primaryCard === true || card?.primary_card === true
}

/** BDMC US primary activate shows inline DOB/SSN when apply returns those fields. PPDC uses CIP. */
export function shouldCollectDobSsnDuringActivate(card, applyCard) {
  if (!card || !applyCard || !isBdmcActivateContext(card)) {
    return false
  }

  const country = String(
    serverData?.locality?.country || serverData?.user?.country || '',
  ).toUpperCase()

  // eslint-disable-next-line i18n-scan/no-country-dependency -- legacy US primary activate inline DOB/SSN gate
  if (country !== 'US' || !isPrimaryActivateCard(card)) {
    return false
  }

  const { status } = applyCard
  if (status === DENIED || status === MANUAL_REVIEW) {
    return false
  }

  const { values = [] } = getApplyResultDetails(applyCard)
  return values.includes('DOB') || values.includes('SSN')
}

/** Legacy activate/enroll routes DENIED + INVALID_AGE to a dedicated ineligible screen. */
export function isInvalidAgeDeniedApplyCard(applyCard) {
  if (!applyCard || applyCard.status !== DENIED) {
    return false
  }

  const { values = [] } = getApplyResultDetails(applyCard)
  return values.length > 0 && values[0] === INVALID_AGE_DENIED_VALUE
}

/**
 * FWC - Card and PIN has always to be set together
 * PPDC - Supports shared PIN, card could have been activated but PIN not set
 * BDMC Instant Access cards may only expose legacy `capabilities` (APPLICATIONS_ACTIVATE)
 * without CCLS-style operationMetadata — treat that as activate-allowed too.
 */
export function isActivateCardAllowed(card) {
  if (hasOperationMetadata(card, { operation: APPLICATIONS, subOperation: ACTIVATE })) {
    return true
  }

  if (!isBdmcActivateContext(card)) {
    return false
  }

  const capabilities = card?.capabilities
  return Array.isArray(capabilities) && capabilities.includes('APPLICATIONS_ACTIVATE')
}

/**
 * BDMC US inline create-PIN after CVV (legacy debitcardnodeweb).
 * PPDC matches develop: activate immediately after CVV, then optional set-pin redirect.
 */
export function shouldCollectPinBeforeActivate(card) {
  if (!card || !isBdmcActivateContext(card)) {
    return false
  }

  const country = String(
    serverData?.locality?.country || serverData?.user?.country || '',
  ).toUpperCase()

  if (country !== 'US') {
    return false
  }

  const pinStatus = card.pinDetails?.status || card.pin_details?.status
  const isPinUnset = !pinStatus || pinStatus === 'UNSET'

  return isPinUnset || !isInstantCard(card)
}

/**
 * PPDC (develop): post-activate redirect to /manage/:id/create-pin when PIN still needed.
 * BDMC: never redirect after activate — legacy always opens activate success (US PIN is
 * collected inline before activate; non-US also lands on success, not set-pin).
 */
export function shouldRedirectToSetPinAfterActivate(card, { pin = undefined } = {}) {
  if (isBdmcActivateContext(card)) {
    return false
  }

  return !pin && shouldSetPin(card, { shouldBeSetByUser: false })
}

/** Jump to card-not-received inside step3; sequential nextStep lands on create-pin after PIN steps were added. */
export function goToActivateCardNotReceivedStep(stepper) {
  stepper.goToNextStep(ACTIVATE_STEP3_SWITCH_NAME, CARD_NOT_RECEIVED_STEP)
}

function scheduleActivateFlowNavigation(navigate) {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(navigate)
    return
  }

  setTimeout(navigate, 0)
}

/** Reads INVALID_CVV / MISMATCHED_CVV from FetchError details or top-level code. */
export function getActivateCvvValidationIssue(error) {
  const { details, code } = error || {}
  if ([INVALID_CVV, MISMATCHED_CVV].includes(code)) {
    return code
  }

  const detailsList = Array.isArray(details) ? details : []
  const matchedDetail = detailsList.find((detail) =>
    [INVALID_CVV, MISMATCHED_CVV].includes(detail?.issue),
  )

  return matchedDetail?.issue
}

/** Return to enter-cvv from later activate steps (confirm-info, create-pin, card-not-received). */
export function goToActivateEnterCredentialStep(stepper) {
  if (!stepper) {
    return
  }

  scheduleActivateFlowNavigation(() => {
    stepper.goToPreviousStep(ENTER_CVV_STEP, undefined, { hideAnimation: true })
  })
}

/**
 * Modal back target when forward navigation skipped intermediates (named jump to card-not-received).
 * null → fall through to linear previousStep.
 */
export function getActivateFlowBackTarget(currentStepName?: string): string | null {
  if (currentStepName === CARD_NOT_RECEIVED_STEP) {
    return ENTER_CVV_STEP
  }

  return null
}

/**
 * Named activate modal Back. Returns true when handled so callers skip linear previousStep.
 */
export function handleActivateFlowBack(stepper): boolean {
  const backTarget = getActivateFlowBackTarget(stepper?.getCurrentStepName?.())
  if (!backTarget) {
    return false
  }

  if (backTarget === ENTER_CVV_STEP) {
    goToActivateEnterCredentialStep(stepper)
    return true
  }

  return false
}

export function getActivateStepAfterCredential(card) {
  return shouldCollectPinBeforeActivate(card) ? CREATE_PIN_STEP : CONFIRM_INFO_STEP
}

export function checkRUGSResponse({ applyCard, card, stepper }) {
  const { status, resultDetails, nonce = '', links = [] } = applyCard
  const { code = '', values = [] } = resultDetails || {}
  const isBdmcActivate = isBdmcActivateContext(card)

  switch (status) {
    case STEPUP_AUTH: {
      const stepUrlQueryParams = getQueryParams()
      const stepUpContext = Array.isArray(values) ? values[0] : values
      // Develop PPDC: only stamp page=enterCvv when card is not shipped.
      // BDMC always returns to enter-cvv after step-up (no auto-submit).
      let additionalQueryParams = {}
      if (isBdmcActivate) {
        additionalQueryParams = { page: ENTER_CVV_STEP }
      } else {
        const { shipmentStatus } = card?.shipmentDetails || {}
        if (isCardNotShipped(shipmentStatus)) {
          additionalQueryParams = { page: ENTER_CVV_STEP }
        }
      }
      const stepUpUrl = constructStepUpRedirectUrl(stepUpContext, MANAGE, {
        ...stepUrlQueryParams,
        ...(nonce && { nonce }),
        ...additionalQueryParams,
      })
      redirectTo(stepUpUrl)
      return true
    }

    /**
     * PPDC US
     * - NEED_MORE_DATA || MANUAL_REVIEW (status) + NEED_MORE_INFO (code) + SSN (value)
     * PPDC DE
     * - NEED_MORE_DATA || MANUAL_REVIEW (status)
     * - Rely on link's title and id
     */
    case NEED_MORE_DATA:
    case MANUAL_REVIEW:
      const defaultOptions = {
        flow: ACTIVATE_CARD,
        additionalReturnUrlParams: nonce ? { nonce } : {},
      }
      // Compliance verification link is not returned for PPDC US
      if (links && links.length > 0) {
        const cipLink = links.find(
          (link) => link.rel === 'redirect' && link.title === 'compliance-verification',
        )
        if (cipLink && cipLink.id) {
          const processBasedCipUrl = generateCipRedirectUrl({
            ...defaultOptions,
            includeReturnUrl: !!code,
            additionalParams: {
              showLandingScreen: !code,
              showAcknowledgementScreen: !code,
            },
            pid: cipLink.id,
          })
          redirectTo(processBasedCipUrl)
          return true
        }
      }

      if (code === NEED_MORE_INFO && values.includes(NEED_MORE_INFO_VALUES.SSN)) {
        const cipRedirectUrl = generateCipRedirectUrl({
          ...defaultOptions,
        })
        redirectTo(cipRedirectUrl)
        return true
      }

      navigateActivateFlowStep(
        stepper,
        status === NEED_MORE_DATA ? NEED_MORE_DATA_STEP : MANUAL_REVIEW_STEP,
      )
      return true

    case DENIED:
      navigateActivateFlowStep(
        stepper,
        isBdmcActivate && isInvalidAgeDeniedApplyCard(applyCard) ? INELIGIBLE_STEP : DENIED_STEP,
      )
      return true

    default:
      throw new Error(`Unhandled apply card RUGS status ${status}`)
  }
}

const ACTIVATE_CARD_FETCH_NAME = 'activateCard'

function isNonUsActivateUser() {
  const country = (serverData?.locality?.country || serverData?.user?.country || 'US').toUpperCase()

  // eslint-disable-next-line i18n-scan/no-country-dependency -- legacy non-US activate soft retry
  return country !== 'US'
}

function shouldSoftRetryNonUsActivateFailure(error, options: any = {}) {
  if (!isBdmcActivateContext(options.card)) {
    return false
  }

  if (!isNonUsActivateUser()) {
    return false
  }

  if (options.fetchName !== ACTIVATE_CARD_FETCH_NAME) {
    return false
  }

  const { getState } = options
  if (!getState || selectActivateSubmitFailed(getState())) {
    return false
  }

  return error instanceof FetchError
}

function handleNonUsActivateSoftRetry(options: any = {}) {
  const { stepper, dispatch, fetchName } = options

  batch(() => {
    dispatch(successfulFetch({ fetchName }))
    dispatch(setActivateSubmitFailed(true))
    dispatch(clearAutoSubmit())
  })
  goToActivateEnterCredentialStep(stepper)
}

export function handleActivateCardError(error, options: any = {}) {
  const { stepper, dispatch, fetchName } = options
  const issue = getActivateCvvValidationIssue(error)
  if ([INVALID_CVV, MISMATCHED_CVV].includes(issue)) {
    batch(() => {
      dispatch(successfulFetch({ fetchName }))
      dispatch(
        setPageError({
          page: 'enterCvv',
          error: 'invalidCvv',
        }),
      )
      dispatch(updateCvv(null))
      dispatch(updateCreatePin(null))
      dispatch(updateConfirmPin(null))
      dispatch(setPinValidated(false))
      dispatch(clearAutoSubmit())
    })
    goToActivateEnterCredentialStep(stepper)
  } else if (shouldSoftRetryNonUsActivateFailure(error, options)) {
    handleNonUsActivateSoftRetry(options)
  } else {
    handleUnexpectedError(error, options)
  }
}

/** Reads activate card id from path `/activate/:cardId`, then query id / cardId. */
export function getActivateCardIdFromLocation() {
  const match = window.location.pathname.match(/\/activate\/([^/]+)\/?$/)
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1])
    } catch {
      return match[1]
    }
  }

  return getQueryParam('id') || getQueryParam('cardId') || ''
}

export function getFlowInputParams() {
  let productName = getQueryParam('productName')
  let directEntry = getQueryParam('directEntry')
  const cardId = getActivateCardIdFromLocation()
  return {
    productName: isValidProductName(ACTIVATE_CARD, productName)
      ? getProductSystemNameByAlias(productName)
      : null,
    directEntry: Boolean(directEntry),
    cardId: cardId || null,
  }
}

export const isValidCvv = (cvv) => {
  return /^[0-9]{3}$/.test(cvv)
}

export const cvvInputMaskOptions = {
  mask: 'CVV',
  blocks: {
    CVV: {
      mask: '000',
    },
  },
  lazy: true,
}

export const goToReplaceCardFlow = (card) => {
  if (!card) return

  const { productName = '' } = card

  if (!productName) return

  const returnUriQueryString = qs.stringify({
    productName: getProductQueryParam(productName),
    page: CARD_NOT_RECEIVED_STEP,
    ...qs.parse(window.location.search),
  })

  const returnUri = window.location.pathname + `?${returnUriQueryString}`

  const replaceCardFlowUrl = createReplaceCardFlowUrl(card, LOST_OR_STOLEN, returnUri)

  if (!replaceCardFlowUrl) {
    return
  }

  goTo(replaceCardFlowUrl)
}

function getActivateCardId(card) {
  return card?.card_id || card?.cardId || ''
}

/** Legacy activate "Send new card": US → summary replace churn; EU → report-lost redirect. */
export function goToActivateSendNewCard(card) {
  if (!card) {
    return
  }

  const cardId = getActivateCardId(card)
  const productName = card.productName || card.product_name || ''
  const isBdmcActivate =
    serverData?.isBdmcExperience === true || productName === BUSINESS_DEBIT_CARD

  if (!isBdmcActivate) {
    goToReplaceCardFlow(card)
    return
  }

  if (!cardId) {
    return
  }

  const country = String(
    serverData?.locality?.country || serverData?.user?.country || '',
  ).toUpperCase()

  // eslint-disable-next-line i18n-scan/no-country-dependency -- legacy US activate send-new-card routes to summary replace
  if (country === 'US') {
    browserRedirectTo(`${prepBdmcSummaryLink()}&flow=replace&cardId=${encodeURIComponent(cardId)}`)
    return
  }

  browserRedirectTo(`${UCS_STUB_URL}/report-lost/${encodeURIComponent(cardId)}`)
}

const ENTER_CVV_STEP_INFO = {
  firstStep: 1,
  firstStepSwitchName: ENTER_CVV_STEP,
}

const ENTER_CVV_AS_FIRST_STEP = {
  ...ENTER_CVV_STEP_INFO,
  hideStepsBeforeFirst: true,
}

const CARD_NOT_RECEIVED_AS_FIRST_STEP = {
  firstStep: ACTIVATE_FLOW_STEP_INDEX.STEP3_SWITCH,
  firstStepSwitchName: CARD_NOT_RECEIVED_STEP,
  hideStepsBeforeFirst: Boolean(getQueryParam('directEntry')),
}

const CONFIRM_INFO_STEP_AS_FIRST_STEP = {
  firstStep: ACTIVATE_FLOW_STEP_INDEX.STEP3_SWITCH,
  firstStepSwitchName: CONFIRM_INFO_STEP,
  hideStepsBeforeFirst: true,
}

export const overrideActivateFlowFirstStepByCardDetails = (card, options: any = {}) => {
  const page = getQueryParam('page')
  const { shipmentDetails: { shipmentStatus = '' } = {}, autoSubmit = false } = card || {}
  const { activationSucceededWithAutoSubmit = false } = options

  if (!card) {
    return { firstStep: -1 }
  }

  // BDMC only: re-render EnterCardDetails after step-up instead of auto-submitting.
  // PPDC matches develop (no forced enter-cvv first-step override from step-up alone).
  if (
    isBdmcActivateContext(card) &&
    isRedirectFromStepUp({}) &&
    !activationSucceededWithAutoSubmit
  ) {
    return ENTER_CVV_AS_FIRST_STEP
  }

  if (autoSubmit) {
    return CONFIRM_INFO_STEP_AS_FIRST_STEP
  }

  // After autoSubmit success keep first step at CONFIRM_INFO so stepper does not jump back to EnterCvv
  if (activationSucceededWithAutoSubmit) {
    return CONFIRM_INFO_STEP_AS_FIRST_STEP
  }

  if (page) {
    switch (page) {
      case CARD_NOT_RECEIVED_STEP:
        // "Didn't get your card?" webview case
        // URL: myaccount/debitcards/activate?productName=CONSUMER_DEBIT_CARD&page=cardNotReceived&directEntry=true&webviewCallback=onReplaceCardComplete&action_type=primary&locale.x=en_US
        if (isCardShipped(shipmentStatus)) {
          return { ...CARD_NOT_RECEIVED_AS_FIRST_STEP, terminalSteps: [ENTER_CVV_STEP] }
        }
        break
      case ENTER_CVV_STEP:
        // To handle after stepup auth has been resolved
        if (isCardNotShipped(shipmentStatus)) {
          return ENTER_CVV_STEP_INFO
        }
        break
      default:
        break
    }
    return ENTER_CVV_AS_FIRST_STEP
  }

  return ENTER_CVV_AS_FIRST_STEP
}

export function getPaypalAppLink() {
  const country = serverData.locality?.country?.toLowerCase()
  return getPayPalUrl(`${country}${GET_PP_APP}`)
}

export const goToSetPinFlow = (card, options: any = {}) => {
  if (!card) return

  const { productName = '' } = card
  const { flowName, hidePinSuccessPage } = options

  if (!productName) return

  const returnUri = getCardManagementPath(card)
  let cancelUri = getQueryParam('returnUri') || PP_SUMMARY

  const additionalParams: any = { returnUri, cancelUri }
  if (isFromEnrollmentFlow(flowName)) {
    additionalParams.intent = INTENT_TYPES.ENROLL
    delete additionalParams.returnUri
  }

  if (getQueryParam('skipPinRedirect')) {
    additionalParams.skipPinRedirect = true
  }

  if (hidePinSuccessPage) {
    additionalParams.hidePinSuccess = true
  }

  const setPinFlowUrl = createSetPinFlowUrl(card, additionalParams)

  if (!setPinFlowUrl) {
    return
  }

  goTo(setPinFlowUrl)
}

export const isActivateFlowInFirstStep = (stepperRef) => {
  const { getCurrentStepName } = stepperRef.current || {}

  if (getCurrentStepName) {
    const currentStepName = getCurrentStepName()
    return [ENTER_CVV_STEP].includes(currentStepName)
  }

  return false
}
